import {
  CalendarPrimeReact,
  DropdownPrimeReactNew,
} from "@finanedi.es/storybook"
import { yupResolver } from "@hookform/resolvers/yup"
import { useCuentasBancariasAll } from "api/CuentaBancaria.hook"
import { RemesaDtoProps } from "api/DTOs/RemesaDto"
import { DEFAULT_CURRENCY_SYMBOL } from "features/Contactos/constants"
import { ImporteBodyTemplate } from "features/Documentos/templates"
import { getDropdownBankValues } from "features/Tesoreria/components/NewRemesaForm/constants"
import { InputText } from "primereact/inputtext"
import { Dispatch, SetStateAction, useEffect, useState } from "react"
import { Controller, Resolver, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import * as yup from "yup"

type RemesaDetailByIdProps = {
  data?: Partial<RemesaDtoProps> | null
  remesaTotal?: number
  onChangeForm: (values: RemesaDtoProps) => void
  onChangeErrors: Dispatch<SetStateAction<boolean>>
}

type FormValues = {
  concepto: string
  fechaVencimiento?: Date | null
  cuentaBancariaId?: string | null
  identificador: string
}

type BankAccount = {
  id?: string
  bic?: string
  identificadores?: { presentador?: string; acreedor?: string }
}

const schema = yup.object({
  concepto: yup.string().nullable().required(),
  fechaVencimiento: yup.date().nullable().required(),
  cuentaBancariaId: yup.mixed().nullable().required(),
  identificador: yup.string().nullable().required(),
})

export const RemesaDetailById = ({
  data,
  remesaTotal,
  onChangeForm,
  onChangeErrors,
}: RemesaDetailByIdProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  const tipo = (data as unknown as { tipo?: number } | undefined)?.tipo

  const { cuentaBancariaAll } = useCuentasBancariasAll({})
  const [selectedAccount, setSelectedAccount] = useState<BankAccount | null>(
    null
  )

  const {
    control,
    setValue,
    formState: { errors },
    watch,
    reset,
  } = useForm<FormValues>({
    resolver: yupResolver(schema) as unknown as Resolver<FormValues>,
    defaultValues: {
      concepto: undefined,
      fechaVencimiento: undefined,
      cuentaBancariaId: undefined,
      identificador: undefined,
    },
  })

  const hasErrors = Boolean(Object.keys(errors).length)

  useEffect(() => {
    if (data) {
      reset({
        concepto: data.concepto ?? "",
        fechaVencimiento: data.fechaVencimiento
          ? new Date(data.fechaVencimiento)
          : undefined,
        cuentaBancariaId: data.cuentaBancariaId ?? null,
        identificador: data.identificador ?? "",
      })
    }
  }, [data, reset])

  useEffect(() => {
    if (cuentaBancariaAll?.items?.length) {
      const items = cuentaBancariaAll.items as unknown as BankAccount[]
      const account = items.find((cta) => cta.id === data?.cuentaBancariaId)
      setSelectedAccount(account ?? null)
    }
  }, [cuentaBancariaAll?.items, data?.cuentaBancariaId])

  const dataWatch = watch()
  const handleBankAccount = (value: BankAccount) => {
    if (!value?.id) return

    setValue("cuentaBancariaId", value.id)
    setSelectedAccount(value)

    if (tipo === 2) {
      if (value?.identificadores?.presentador) {
        setValue("identificador", value.identificadores.presentador)
      }
    } else if (value?.identificadores?.acreedor) {
      setValue("identificador", value.identificadores.acreedor)
    }
  }

  useEffect(() => {
    onChangeForm({
      concepto: dataWatch?.concepto,
      fechaVencimiento: dataWatch?.fechaVencimiento,
      cuentaBancariaId: dataWatch?.cuentaBancariaId,
      identificador: dataWatch?.identificador,
    } as unknown as RemesaDtoProps)
    onChangeErrors(hasErrors)
  }, [
    dataWatch?.concepto,
    dataWatch?.fechaVencimiento,
    dataWatch?.cuentaBancariaId,
    dataWatch?.identificador,
    onChangeForm,
    onChangeErrors,
    hasErrors,
  ])

  return (
    <form>
      <div className='mb-3 p-fluid'>
        <div className='subtitle_container'>
          <h6 className='text__simple'>{t("remesas.edit.form.title")}</h6>
          <ImporteBodyTemplate
            importe={remesaTotal}
            divisa={DEFAULT_CURRENCY_SYMBOL}
          />
        </div>
      </div>

      <div className='mb-3 select_container'>
        <label htmlFor='concepto'>{t("remesas.edit.form.conceptLabel")}</label>
        <InputText
          id='concepto'
          type='text'
          value={dataWatch?.concepto ?? ""}
          onChange={(e) =>
            setValue("concepto", e.target.value, { shouldValidate: true })
          }
        />
        {errors.concepto && (
          <div className='tw-text-danger tw-text-left tw-text-xs'>
            {t("errors.selectedOnePlease")}
          </div>
        )}
      </div>

      <div className='mb-3 select-main_content'>
        <div className='select_container'>
          <label htmlFor='fechaVencimiento'>
            {t("remesas.edit.form.dateMaturityLabel")}
          </label>
          <Controller
            control={control}
            name='fechaVencimiento'
            rules={{ required: true }}
            render={({ field }) => (
              <CalendarPrimeReact
                id='fechaVencimiento'
                value={field.value as Date | null}
                onChange={(event) => {
                  if (event && event?.value) field.onChange(event.value)
                }}
                locale={language}
                placeholder='dd/mm/aaaa'
                dateFormat='dd/mm/yy'
                className='tw-w-full'
                showIcon
              />
            )}
          />
          {errors.fechaVencimiento && (
            <div className='tw-text-danger tw-text-left tw-text-xs'>
              {t("errors.dateRequired")}
            </div>
          )}
        </div>
      </div>

      <div className='mb-3 select_container'>
        <label htmlFor='cuentaBancariaId'>
          {t("remesas.edit.form.accountLabel")}
        </label>
        <Controller
          control={control}
          name='cuentaBancariaId'
          rules={{ required: true }}
          render={({ field }) => (
            <div>
              <DropdownPrimeReactNew
                options={getDropdownBankValues(cuentaBancariaAll?.items)}
                value={selectedAccount}
                onChange={(e) => {
                  const items =
                    cuentaBancariaAll?.items as unknown as BankAccount[]
                  const account = items?.find(
                    (cta) => cta.id === e.target.value.id
                  )
                  if (account) handleBankAccount(account)
                  field.onChange(e.target.value)
                }}
                placeholder={`${t("remesas.edit.form.placeholderAccount")}`}
                emptyFilterMessage={t("remesas.edit.form.emptyMsg")}
                emptyMessage={t("remesas.edit.form.emptyMsg")}
                id='cuentaBancariaId'
                name='cuentaBancariaId'
                className='tw-w-full'
              />
            </div>
          )}
        />
        {errors.cuentaBancariaId && (
          <div className='tw-text-danger tw-text-left tw-text-xs'>
            {t("errors.selectedOnePlease")}
          </div>
        )}
      </div>

      <div className='mb-3 select_container'>
        <label>{t("remesas.edit.form.swiftBicLabel")}</label>
        <InputText
          value={selectedAccount?.bic ?? ""}
          disabled
        />
      </div>

      <div className='mb-3 select_container'>
        <label htmlFor='identificador'>
          {tipo === 2
            ? t("remesas.edit.form.idRepresentativeLabel")
            : t("remesas.edit.form.idCreditorLabel")}
        </label>
        <InputText
          id='identificador'
          type='text'
          value={dataWatch?.identificador ?? ""}
          onChange={(e) =>
            setValue("identificador", e.target.value, { shouldValidate: true })
          }
        />
        {errors.identificador && (
          <div className='tw-text-danger tw-text-left tw-text-xs'>
            {t("errors.addId")}
          </div>
        )}
      </div>
    </form>
  )
}
